import '../../base/loader/index.js';
import '../../base/modal/index.js';
import '../../base/header/index.js';
import '../dataset_selection_options/index.js';

import {html} from 'lit';
import {customElement, property, query} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import styles from './style.scss';
import {BrandIcon} from '#lit/components/base/brand_button/index.js';
import type {TeamPopulated} from '#lit/types/teams.js';
import type FlyModal from '#lit/components/base/modal/index.js';
import {AvatarColorVariants} from '#lit/components/base/avatar/index.js';
import {classMap} from 'lit/directives/class-map.js';
import type {Dataset, DatasetGroup} from '#lit/types/datasets.js';
import type {SerializedData} from '#lit/types/serializer.js';
import {getValueByHash} from '#lit/utils/arrays.js';

export interface TeamDatasetsResponse {
  data: SerializedData<Omit<Dataset, 'id'>>[];
}

export const AUDIENCES_CREATE_BUTTON_ELEMENT_TAG = 'audiences-create-button';
const DEFAULT_REDIRECT_PATH = '/audience_templates';
const DEFAULT_TEXT = 'New Audience';

/**
 * Button for creatin' audiences
 */
@customElement(AUDIENCES_CREATE_BUTTON_ELEMENT_TAG)
export default class AudiencesCreateButton extends FlywheelComponent {
  @property({type: Array})
  teams: TeamPopulated[] = [];

  @property({type: Array})
  datasets: Dataset[] = [];

  @property({attribute: 'dataset-groups', type: Array})
  datasetGroups: DatasetGroup[] = [];

  @property({type: Number, attribute: false})
  selected?: number;

  @property({type: Boolean})
  showModal = false;

  @property()
  redirectPath: string = DEFAULT_REDIRECT_PATH;

  @query('fly-modal')
  modal?: FlyModal;

  @property({type: Boolean})
  fetching = false;

  @property({type: Boolean})
  redirectingToDatasetGroup = false;

  @property({type: String})
  text = DEFAULT_TEXT;

  static get styles() {
    return [styles];
  }

  get onlyDatasets() {
    return this.selected && this.teams.length == 1;
  }

  get fetchingDatasets() {
    return this.selected && this.fetching;
  }

  get smartRedirectPath(): string {
    const [datasetGroup] = this.datasetGroups;
    if (!this.redirectingToDatasetGroup || !datasetGroup) {
      return this.redirectPath;
    }

    return `/v2/dataset_groups/${datasetGroup.id}/audiences/new`;
  }

  buttonClickHandler() {
    if (!this.showModal) {
      location.href = this.smartRedirectPath;
      return;
    }

    if (this.modal) {
      this.modal.openModal();
    }
  }

  teamSelectionHandler(team: TeamPopulated) {
    this.selected = team.id;

    this.fetching = false;
    if (this.datasetGroups.length === 1) {
      const [onlyGroup] = this.datasetGroups;
      this.datasetSelectionHandler(onlyGroup);
    } else {
      setTimeout(() => {
        this.fetching = false;
      }, 1000);
    }
  }

  datasetSelectionHandler(group: DatasetGroup) {
    location.href = `/v2/dataset_groups/${group.id}/audiences/new`;
  }

  renderTeam(team: TeamPopulated) {
    const teamClasses = classMap({
      'selection__option--selected': this.selected === team.id,
    });

    return html`
      <div
        class="selection__option ${teamClasses}"
        @click="${() => this.teamSelectionHandler(team)}"
      >
        <div class="selection__option__avatar">
          <fly-avatar
            name="${team.name}"
            variant="${AvatarColorVariants.PRIMARY}"
            circle
          ></fly-avatar>
        </div>
        <div class="selection__option__info">
          <div class="selection__option__info__org">
            ${team.organization.name}
          </div>
          ${team.name}
        </div>
      </div>
    `;
  }

  load() {
    if (this.teams.length === 1) {
      this.selected = this.teams[0].id;
    }
  }

  colorVariant(name: string): AvatarColorVariants {
    return getValueByHash<AvatarColorVariants>(
      Object.values(AvatarColorVariants),
      name
    );
  }

  render() {
    return html`
      <fly-brand-button
        icon=${BrandIcon.CREATE}
        alt="New audience button"
        @click=${() => this.buttonClickHandler()}
      >
        ${this.text}
      </fly-brand-button>
      <fly-modal
        label="${
          this.onlyDatasets || this.fetchingDatasets
            ? 'Select a Dataset Group to Create Audience'
            : 'Team Selection'
        }"
        >
        <fly-loader
          ?loading=${this.fetching}
          ?initWithLoader=${false}
          style="--fly-loader-margin: 24px auto"
        >
          ${
            !this.selected || this.fetching
              ? html`
                  Select a team below to create your audience.
                  <div class="selection">
                    ${this.teams.map(team => {
                      return this.renderTeam(team);
                    })}
                  </div>
                `
              : html`
                  <dataset-selection-options
                    href="/audiences/new"
                    .datasetGroups=${this.datasetGroups}
                  >
                  </dataset-selection-options>
                `
          }
          </fly-loader>
        </div>
        <div slot="header-aside">
          <fly-icon icon="close"></fly-icon>
        </div>
      </fly-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'audiences-create-button': AudiencesCreateButton;
  }
}
